import { CLIENT_LISTING_PATH } from "constants/app-paths"
import { t } from "i18next"
import { capitalizeFirstLetter } from "utils"
import { getURLString, getUserMappingKey } from "utils/reports"
import { customDateFormat, dateTimeFunction } from "utils/date"

const getUserData = (item, parentId) => {
  let obj
  if (item?.data) {
    obj = {
      ...item.data,
      id: item.data.userId,
      name: item.data.userName,
      parentId: parentId,
      joiningDate: customDateFormat(dateTimeFunction({ date: item?.data?.joiningDate, Default: true })),
      ...(item?.children && { children: item.children.map(e => getUserData(e, parentId)) }),
    }
  } else {
    obj = {
      ...item,
      type: "region",
      id: item?.cities ? "region-" + item?.regionId : item?.cityId,
      name: item?.cityName || item.regionName,
      parentId: parentId,
      ...(item?.cities && {
        children: item.cities.map(e => getUserData(e, item?.cities ? "region-" + item?.regionId : item?.cityId)),
      }),
    }
  }
  return obj
}

const getNestedUserData = items => {
  return items?.length <= 1 ? (!!items?.[0]?.children?.length ? getNestedUserData(items?.[0]?.children) : items) : items
}

export const hierarchicalStaffTransformer = staffArray => {
  const transformedArray = staffArray.map(item => {
    const { data, children } = item
    return {
      title: data?.name,
      value: data?.id?.toString(),
      key: data?.id.toString(),
      regionId: data?.regionId,
      ...(children?.length && { children: hierarchicalStaffTransformer(children) }),
    }
  })
  return transformedArray
}

export const analyticsTransformer = response => {
  let listKeys = []
  Object.keys(response?.comparison?.staticKeys).forEach((item, index) => {
    listKeys.push({
      key: item,
      label: t(capitalizeFirstLetter(item)),
      title: t(capitalizeFirstLetter(item)),
      children: [],
    })
    Object.keys(response?.comparison?.staticKeys[item]).forEach(e => {
      listKeys[index].children.push({
        ...response?.comparison?.staticKeys[item][e],
        key: e,
        parent: item,
      })
    })
  })

  return {
    ...response,
    listKeys,
    defaultComparison: [
      { label: t("Qualified Calls"), short: "Q Calls", key: "qualifiedCalls", parent: "calls" },
      { label: t("Meetings Done"), short: "M D", key: "meetingsDone", parent: "meetings" },
    ],
  }
}

export const userKpiTransformer = (response, req, params) => {
  let listKeys = []
  let filteredColumns = ""

  const parentKeys = Object.keys(response?.userKpi?.staticKeys || {})
    .map(parentKey => ({
      parentKey,
      order: response.userKpi.staticKeys[parentKey]?.order ?? 0,
    }))
    .sort((a, b) => a.order - b.order)

  parentKeys.map(({ parentKey }) => {
    const children = Object.keys(response?.userKpi?.staticKeys[parentKey] || {})
      .filter(childKey => childKey !== "order") // ignore order key
      .map(childKey => {
        const { order, ...childData } = response.userKpi.staticKeys[parentKey][childKey] || {}
        if (childData?.default === true) {
          filteredColumns += (filteredColumns ? "," : "") + childKey
        }
        return {
          type: childData?.type ? childData.type : childData?.hover ? "hover" : "number",
          ...childData,
          key: childKey,
          slopeKey: `${childKey}Slope`,
          parent: parentKey,
          hoverKeys: childData?.hover ? childData?.hoverKeys : {},
          urlMapping: {
            url: getURLString(response?.filters, params, childData, parentKey, childKey),
            userMappingKey: getUserMappingKey(childData),
          },
          target: childData?.target,
          order: order ?? 0,
        }
      })
      .sort((a, b) => a.order - b.order)
    listKeys.push({
      key: parentKey,
      label: t(capitalizeFirstLetter(parentKey)),
      title: t(capitalizeFirstLetter(parentKey)),
      children,
    })
  })

  const userKpi = {
    ...response.userKpi,
    data: response.userKpi.data.map(item => getUserData(item, item?.data?.userId || item?.regionId)),
  }

  return {
    ...response,
    listKeys,
    userKpi,
    staffRegionComparison: { data: getNestedUserData(response?.userKpi?.data.map(item => getUserData(item))) },
    defaultComparison: [
      { label: t("Qualified Calls"), short: "Q Calls", key: "qualifiedCalls", parent: "calls" },
      { label: t("Meetings Done"), short: "M D", key: "meetingsDone", parent: "meetings" },
    ],
    defaultFilters: { filteredColumns },
  }
}

export const clientsTransformer = (response, args) => {
  const allocatedToQuery = args?.userIds ? `&allocatedTo=${args?.userIds}` : ``
  const funnel = {
    extra: {
      title: args?.staffType == "average" ? t("Avg Closed Lost Per User") : t("Closed Lost"),
      percentage: response?.clients?.closedlostPercentage,
      count: response?.clients?.closedlostCount,
    },
    list: [
      {
        label: t("Inquiry"),
        key: "inquiry",
        count: response?.clients?.inquiryCount,
        percentage: response?.clients?.inquiryPercentage,
        backgroundColor: "#8fa2c6",
        redirectRoute: `/en${CLIENT_LISTING_PATH}?sortBy=id&sortOrder=desc&activeTab=inquiry&page=0${allocatedToQuery}`,
      },
      {
        label: t("Prospect"),
        key: "prospect",
        count: response?.clients?.prospectCount,
        percentage: response?.clients?.prospectPercentage,
        backgroundColor: "#7c99ab",
        redirectRoute: `/en${CLIENT_LISTING_PATH}?sortBy=id&sortOrder=desc&activeTab=prospect&page=0${allocatedToQuery}`,
      },
      {
        label: t("Mature"),
        key: "mature",
        count: response?.clients?.matureCount,
        percentage: response?.clients?.maturePercentage,
        potentialRevenue: response?.clients?.matureProjectedRevenue,
        backgroundColor: "#75c9a2",
        redirectRoute: `/en${CLIENT_LISTING_PATH}?sortBy=id&sortOrder=desc&activeTab=mature&page=0${allocatedToQuery}`,
      },
      {
        label: t("Pre - Closure"),
        key: "pre_closure",
        count: response?.clients?.preclosureCount,
        percentage: response?.clients?.preclosurePercentage,
        potentialRevenue: response?.clients?.preclosureProjectedRevenue,
        backgroundColor: "#509f8f",
        redirectRoute: `/en${CLIENT_LISTING_PATH}?sortBy=id&sortOrder=desc&activeTab=pre_closure&page=0${allocatedToQuery}`,
      },
      {
        label: t("Sold"),
        key: "sold",
        count: response?.clients?.soldCount,
        percentage: response?.clients?.soldPercentage,
        potentialRevenue: response?.clients?.soldProjectedRevenue,
        backgroundColor: "#1c6758",
        redirectRoute: `/en${CLIENT_LISTING_PATH}?sortBy=id&sortOrder=desc&activeTab=sold&page=0${allocatedToQuery}`,
      },
    ],
  }

  return {
    ...response,
    funnel,
    tabExtraHeader: {
      clients: {
        title: t("Total Clients"),
        percentage: response?.clients?.closedlostPercentage,
        subValue: response?.clients?.closedlostCount,
        subTitle: args?.staffType == "average" ? t("Avg Closed Lost Per User") : t("Closed Lost"),
        value: response?.clients?.totalClients,
      },
    },
  }
}

const pipelineTransformer = response => {
  if (response?.pipeline) {
    const pipelineData = []
    const pipelineLeads = []
    Object.keys(response?.pipeline?.staticKeys?.bars)?.map(e => {
      let data = [
        {
          ...response?.pipeline?.[e],
          dataType: response?.pipeline?.staticKeys?.bars?.[e],
          type: "Probable Revenue",
          value: response?.pipeline?.[e]?.probable || 0,
        },
        {
          ...response?.pipeline?.[e],
          dataType: response?.pipeline?.staticKeys?.bars?.[e],
          type: "Potential Revenue",
          value: response?.pipeline?.[e]?.potential || 0,
        },
      ]
      pipelineLeads.push({
        ...response?.pipeline?.[e],
        dataType: response?.pipeline?.staticKeys?.bars?.[e],
        type: "Leads",
        value: response?.pipeline?.[e]?.leads || 0,
      })
      pipelineData.push(...data)
    })

    return {
      ...response,
      tabExtraHeader: {
        revenue: {
          subTitle: t("Probable Revenue"),
          subValue: response?.pipeline?.totalProbableRevenue,
          title: t("Potential Revenue"),
          value: response?.pipeline?.totalPotentialRevenue,
        },
        leads: {
          title: t("Total Leads"),
          value: response?.pipeline?.totalLeads,
        },
      },
      pipeline: { ...response?.pipeline },
      revenue: pipelineData,
      leads: pipelineLeads,
    }
  }
  return { ...response }
}

export const clientsAndPipelineTransformer = (response, req, params) => {
  if (params.load === "clients") {
    return clientsTransformer(response, params)
  } else if (params.load === "pipeline") {
    return pipelineTransformer(response)
  }
  return response
}

export const callsMeetingsTransformer = (response, req, params) => {
  const loadType = params?.load || null
  const callsObj = { ...response?.calls?.staticKeys?.bars }

  const callKeys = Object.keys(callsObj)
  const uniqueCallsData =
    (loadType == "calls" &&
      callKeys.map(e => {
        return {
          ...response?.calls?.[e],
          callType: callsObj?.[e],
          type: t("Unique"),
          value: response?.calls?.[e]?.unique || 0,
          total: response?.calls?.[e]?.total,
        }
      })) ||
    {}

  const duplicateCallsData =
    (loadType == "calls" &&
      callKeys?.map(e => {
        return {
          ...response?.calls?.[e],
          callType: callsObj?.[e],
          type: t("Repeat"),
          value: response?.calls?.[e]?.total - response?.calls?.[e]?.unique || 0,
          total: response?.calls?.[e]?.total,
        }
      })) ||
    {}

  const uniqueMeetingData =
    (loadType == "meetings" &&
      Object.keys(response?.meetings?.staticKeys?.bars)?.map(e => {
        return {
          ...response?.meetings?.[e],
          meetingType: response?.meetings?.staticKeys?.bars?.[e],
          type: t("Unique"),
          value: response?.meetings?.[e]?.unique,
          total: response?.meetings?.[e]?.total,
        }
      })) ||
    {}

  const duplicateMeetingData =
    (loadType == "meetings" &&
      Object.keys(response?.meetings?.staticKeys?.bars)?.map(e => {
        return {
          ...response?.meetings?.[e],
          meetingType: response?.meetings?.staticKeys?.bars?.[e],
          type: t("Repeat"),
          value: response?.meetings?.[e]?.total - response?.meetings?.[e]?.unique,
          total: response?.meetings?.[e]?.total,
        }
      })) ||
    {}
  const meetingData = loadType == "meetings" ? duplicateMeetingData.concat(uniqueMeetingData) : {} //concat the above two arrays for meeting-bar-chart
  const callData = loadType == "calls" ? duplicateCallsData.concat(uniqueCallsData) : {} //concat the above two arrays for call-bar-chart
  return {
    ...response,
    calls: { ...response?.calls, total: response?.calls?.totalCalls, avgPerDay: response?.calls?.avgCallsDay },
    meetings: {
      ...response?.meetings,
      total: response?.meetings?.meetingsDone,
      avgPerDay: response?.meetings?.avgMeetingsDay,
    },
    callData: callData,
    meetingData: meetingData,
  }
}
